import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Grid,
  Select,
  MenuItem as SelectMenuItem,
  Autocomplete,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";
import { hasPermissionDirect } from "../../../utils/hasPermissions";
import ActionsMenu from "./ActionsMenu";
import axios from "axios";
import PropTypes from "prop-types";

export default function StaffExternal() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // State variables
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [courseCodes, setCourseCodes] = useState([]);
  const [courseCodesLoading, setCourseCodesLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [departments, setDepartments] = useState([]);
  const [staffFormData, setStaffFormData] = useState({
    salutation: "",
    staff_name: "",
    email: "",
    designation: "",
    experience: "",
    department_id: "",
    qualification: "",
    aadhar_number: "",
    personal_mobile: "",
    alternative_mobile: "",
    personal_address: "",
    college_name: "",
    college_address: "",
    referenced_by: "",
    course_code: "",
    staff_status: "Active",
    workflow_status: "DRAFT",
    college_id: "",
    confirm_email: "",
    confirm_personal_mobile: "",
  });
  const [staffFormErrors, setStaffFormErrors] = useState({});

  // Get user data from session storage (memoized to prevent infinite loops)
  const user = useMemo(() => {
    const userData = sessionStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  }, []);

  // Helper functions
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return {
      Authorization: `Bearer ${token}`,
    };
  };
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/staff-external/departments/${user.client_id}/${user.college_id}`
      );

      if (response.data.success) {
        setDepartments(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to load departments:", error);

      setDepartments([]);
    }
  };
  useEffect(() => {
    if (user?.college_id) {
      fetchDepartments();
    }
  }, [user?.college_id]);
  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const isValidAadhar = (aadhar) => /^\d{12}$/.test(aadhar);
  const isValidMobile = (mobile) => /^\d{10}$/.test(mobile);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Status cell component
  const ActiveStatusCell = ({ value, row }) => {
    const handleStatusToggle = async () => {
      const newStatus = value === "Active" || value === "ACTIVE" ? "INACTIVE" : "ACTIVE";

      try {
        const response = await axios.put(
          `${API_BASE_URL}/api/staff-external/${user.client_id}/${user.college_id}/${row.external_staff_id}`,
          { staff_status: newStatus, modified_by: user?.user_email || "unknown" },
          { headers: getAuthHeaders() }
        );

        if (response.status === 200) {
          showSnackbar(`Status updated to ${newStatus}`, "success");
          fetchData();
        }
      } catch (error) {
        showSnackbar("Error updating status", "error");
        console.error("Status update error:", error);
      }
    };

    return (
      <Chip
        label={value === "Active" || value === "ACTIVE" ? "ACTIVE" : "INACTIVE"}
        color={value === "Active" || value === "ACTIVE" ? "success" : "error"}
        size="medium"
        variant="outlined"
        onClick={handleStatusToggle}
        sx={{
          cursor: "pointer",
          "&:hover": {
            opacity: 0.8,
            transform: "scale(1.05)",
          },
        }}
      />
    );
  };

  ActiveStatusCell.propTypes = {
    value: PropTypes.string.isRequired,
    row: PropTypes.object.isRequired,
  };

  // Fetch data (memoized to prevent unnecessary re-renders)
  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/staff-external/${user.client_id}/${user.college_id}`,
        { headers: getAuthHeaders() }
      );

      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      }

      const formattedData = data.map((item) => ({
        id: item.external_staff_id,
        ...item,
      }));

      setAllRows(formattedData);
      setRows(formattedData);
    } catch (error) {
      showSnackbar("Error fetching staff data", "error");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch course codes for dropdown
  const fetchCourseCodes = useCallback(async () => {
    if (!user) return;

    setCourseCodesLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/course-codes/${user.client_id}/${user.college_id}`,
        { headers: getAuthHeaders() }
      );

      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setCourseCodes(response.data.data);
      } else {
        setCourseCodes([]);
      }
    } catch (error) {
      console.error("Error fetching course codes:", error);
      setCourseCodes([]);
    } finally {
      setCourseCodesLoading(false);
    }
  }, [user]);

  // Menu handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Form handlers
  const handleOpenAdd = () => {
    setEditing(null);
    setStaffFormData({
      salutation: "",
      staff_name: "",
      department_id: "",
      email: "",
      designation: "",
      qualification: "",
      experience: "",
      aadhar_number: "",
      personal_mobile: "",
      alternative_mobile: "",
      personal_address: "",
      college_name: "",
      college_address: "",
      referenced_by: "",
      course_code: "",
      staff_status: "Active",
      workflow_status: "DRAFT",
    });
    setStaffFormErrors({});
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditing(row);
    setStaffFormData({
      salutation: row.salutation || "",
      staff_name: row.staff_name || "",
      email: row.email || "",
      department_id: row.department_id || "",
      experience: row.experience || "",
      designation: row.designation || "",
      qualification: row.qualification || "",
      aadhar_number: row.aadhar_number || "",
      personal_mobile: row.personal_mobile || "",
      alternative_mobile: row.alternative_mobile || "",
      personal_address: row.personal_address || "",
      college_name: row.college_name || "",
      college_address: row.college_address || "",
      referenced_by: row.referenced_by || "",
      course_code: row.course_code || "",
      staff_status: row.staff_status || "Active",
      workflow_status: row.workflow_status || "DRAFT",
    });
    setStaffFormErrors({});
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this staff member?")) return;

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/staff-external/${user.client_id}/${user.college_id}/${id}`,
        { headers: getAuthHeaders() }
      );

      if (response.status === 200) {
        showSnackbar("Staff deleted successfully", "success");
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting staff:", error);
      showSnackbar("Error deleting staff", "error");
    }
  };

  const preventCopyPaste = {
    onCopy: (e) => e.preventDefault(),
    onCut: (e) => e.preventDefault(),
    onPaste: (e) => e.preventDefault(),
    autoComplete: "off",
  };
  const handleSubmit = async () => {
    const errors = {};

    // Validation
    if (!staffFormData.staff_name?.trim()) {
      errors.staff_name = "Staff Name is mandatory";
    } else if (staffFormData.staff_name.length > 100) {
      errors.staff_name = "Staff Name cannot exceed 100 characters";
    }

    if (!staffFormData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!isValidEmail(staffFormData.email)) {
      errors.email = "Please enter a valid email format";
    }

    if (!staffFormData.aadhar_number?.trim()) {
      errors.aadhar_number = "Aadhar Number is required";
    } else if (!isValidAadhar(staffFormData.aadhar_number)) {
      errors.aadhar_number = "Aadhar Number must be exactly 12 digits";
    }

    if (staffFormData.personal_mobile?.trim() && !isValidMobile(staffFormData.personal_mobile)) {
      errors.personal_mobile = "Mobile Number must be exactly 10 digits";
    }

    if (
      staffFormData.alternative_mobile?.trim() &&
      !isValidMobile(staffFormData.alternative_mobile)
    ) {
      errors.alternative_mobile = "Alternative Mobile Number must be exactly 10 digits";
    }

    if (!staffFormData.confirm_email) {
      errors.confirm_email = "Please confirm email";
    } else if (
      staffFormData.email.trim().toLowerCase() !== staffFormData.confirm_email.trim().toLowerCase()
    ) {
      errors.confirm_email = "Email does not match";
    }

    if (!staffFormData.confirm_personal_mobile) {
      errors.confirm_personal_mobile = "Please confirm mobile number";
    } else if (
      staffFormData.personal_mobile.trim() !== staffFormData.confirm_personal_mobile.trim()
    ) {
      errors.confirm_personal_mobile = "Mobile number does not match";
    }

    setStaffFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const userEmail = user?.user_email || "unknown";

    // Transform enum values to match database expectations
    const formDataForAPI = {
      ...staffFormData,
      staff_status: staffFormData.staff_status === "ACTIVE" ? "ACTIVE" : "INACTIVE",
      workflow_status:
        staffFormData.workflow_status === "APPROVED"
          ? "APPROVED"
          : staffFormData.workflow_status === "DRAFT"
          ? "DRAFT"
          : "INACTIVE",
    };

    try {
      let response;
      if (editing) {
        const updateData = { ...formDataForAPI, modified_by: userEmail };

        response = await axios.put(
          `${API_BASE_URL}/api/staff-external/${user.client_id}/${user.college_id}/${editing.external_staff_id}`,
          updateData,
          { headers: getAuthHeaders() }
        );
      } else {
        const createData = { ...formDataForAPI, created_by: userEmail };

        response = await axios.post(
          `${API_BASE_URL}/api/staff-external/${user.client_id}/${user.college_id}`,
          createData,
          { headers: getAuthHeaders() }
        );
      }
      if (response.status === 200 || response.status === 201) {
        showSnackbar(response.data.message || "Saved successfully", "success");
      } else {
        showSnackbar("Something went wrong. Please try again.", "error");
      }
      setOpen(false);
      fetchData();
    } catch (error) {
      showSnackbar("Error saving Staff External", "error");
    }
  };

  // CSV Download function
  const handleDownloadCsv = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/staff-external/${user.client_id}/${user.college_id}/download/csv`,
        {
          headers: getAuthHeaders(),
          responseType: "blob", // Important for file download
        }
      );

      // Create blob URL and download
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "staff_external.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar("CSV downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading CSV:", error);
      // Fallback: Generate CSV from current data
      generateCsvFromData();
    }
  };

  // Fallback function to generate CSV from current data
  const generateCsvFromData = () => {
    try {
      const csvHeaders = [
        "ID",
        "Salutation",
        "Staff Name",
        "Designation",
        "Qualification",
        "Email",
        "Aadhar Number",
        "Personal Mobile",
        "Alternative Mobile",
        "Personal Address",
        "College Name",
        "College Address",
        "Referenced By",
        "Course Code",
        "Staff Status",
        "Workflow Status",
      ];

      const csvData = allRows.map((row) => [
        row.external_staff_id || "",
        row.salutation || "",
        row.staff_name || "",
        row.designation || "",
        row.qualification || "",
        row.email || "",
        row.aadhar_number || "",
        row.personal_mobile || "",
        row.alternative_mobile || "",
        row.personal_address || "",
        row.college_name || "",
        row.college_address || "",
        row.referenced_by || "",
        row.course_code || "",
        row.staff_status || "",
        row.workflow_status || "",
      ]);

      // Create CSV content
      const csvContent = [
        csvHeaders.join(","),
        ...csvData.map((row) => row.map((field) => `"${field}"`).join(",")),
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "staff_external.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar("CSV downloaded successfully", "success");
    } catch (error) {
      console.error("Error generating CSV:", error);
      showSnackbar("Error downloading CSV", "error");
    }
  };
  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/staff-external/${user.client_id}/${user.college_id}/${user.user_email}/upload/csv`,
        formData,
        { headers: getAuthHeaders() }
      );
      showSnackbar(data.message || "CSV uploaded successfully", "success");
      fetchData();
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Upload failed", "error");
    } finally {
      e.target.value = "";
    }
  };

  // PDF Download function
  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/staff-external/${user.client_id}/${user.college_id}/download/pdf`,
        {
          headers: getAuthHeaders(),
          responseType: "blob", // Important for file download
        }
      );

      // Create blob URL and download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "staff_external.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar("PDF downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      showSnackbar("Error downloading PDF. Please try again.", "error");
    }
  };

  // Effects
  useEffect(() => {
    if (user) {
      fetchData();
      fetchCourseCodes();
    }
  }, [user, fetchData, fetchCourseCodes]);

  // Local search filter
  useEffect(() => {
    if (!search) setRows(allRows);
    else {
      const s = search.toLowerCase();
      setRows(
        allRows.filter(
          (r) =>
            r.staff_name?.toLowerCase().includes(s) ||
            r.email?.toLowerCase().includes(s) ||
            r.college_name?.toLowerCase().includes(s) ||
            r.course_code?.toLowerCase().includes(s)
        )
      );
    }
  }, [search, allRows]);

  // Define columns for DataGrid
  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 80,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
      },
      { field: "id", headerName: "ID", flex: 0.5, minWidth: 50 },
      { field: "salutation", headerName: "Salutation", flex: 0.8, minWidth: 80 },
      { field: "staff_name", headerName: "Staff Name", flex: 1.5, minWidth: 150 },
      { field: "designation", headerName: "Designation", flex: 1.3, minWidth: 130 },
      { field: "qualification", headerName: "Qualification", flex: 1.3, minWidth: 130 },
      { field: "email", headerName: "Email", flex: 2, minWidth: 300 },

      { field: "personal_mobile", headerName: "Mobile", flex: 1.2, minWidth: 120 },
      {
        field: "department",
        headerName: "Department",
        flex: 1.5,
        minWidth: 160,
      },
      { field: "alternative_mobile", headerName: "Alt. Mobile", flex: 1.2, minWidth: 120 },
      { field: "aadhar_number", headerName: "Aadhar Number", flex: 1.2, minWidth: 180 },
      { field: "college_name", headerName: "College Name", flex: 1.5, minWidth: 220 },
      { field: "course_code", headerName: "Course Code", flex: 1.2, minWidth: 120 },
      {
        field: "staff_status",
        headerName: "Staff Status",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => <ActiveStatusCell value={params.value} row={params.row} />,
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        flex: 1,
        minWidth: 130,
        renderCell: (params) => (
          <ActionsMenu
            row={params.row}
            onEdit={() => handleOpenEdit(params.row)}
            onDelete={() => handleDelete(params.row.external_staff_id)}
            hasPermissionDirect={hasPermissionDirect}
          />
        ),
      },
    ],
    []
  );
  const textFieldSx = {
    "& .MuiInputBase-input": {
      fontSize: "1.125rem",
    },
    "& .MuiInputLabel-root": {
      fontSize: "1.125rem",
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.95rem",
    },
  };
  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            EXTERNAL STAFF MANAGEMENT
          </Typography>
          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={handleMenuOpen}
          >
            ACTIONS
          </Button>
          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#115293" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleOpenAdd();
                }}
              >
                ADD STAFF
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: "#4caf50",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#357a38" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadCsv();
                }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                sx={{
                  backgroundColor: "#e53935",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c62828" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadPdf();
                }}
              >
                DOWNLOAD PDF
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  backgroundColor: "#ff9800",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c77700" },
                }}
              >
                UPLOAD CSV
                <input
                  hidden
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    handleMenuClose();
                    handleUploadCsv(e);
                  }}
                />
              </Button>
            </MenuItem>
          </Menu>
          <TextField
            size="medium"
            placeholder="Search data"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => fetchData()}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, ml: 2, mr: 2, mb: 2 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            pagination
            disableSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Box>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Edit External Staff" : "Add External Staff"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Salutation */}
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Salutation"
                size="medium"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.salutation}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    salutation: e.target.value,
                  })
                }
                SelectProps={{ native: true }}
              >
                <option value="">Select Salutation</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Dr.">Dr.</option>
                <option value="Prof.">Prof.</option>
              </TextField>
            </Grid>

            {/* Staff Name */}
            <Grid item xs={12} sm={8}>
              <TextField
                label="Staff Name"
                required
                fullWidth
                size="medium"
                sx={textFieldSx}
                value={staffFormData.staff_name}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    staff_name: e.target.value,
                  })
                }
                error={!!staffFormErrors.staff_name}
                helperText={staffFormErrors.staff_name}
              />
            </Grid>

            {/* Designation */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Designation"
                size="medium"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.designation}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    designation: e.target.value,
                  })
                }
                SelectProps={{ native: true }}
              >
                <option value="">Select Designation</option>
                <option value="Principal">Principal</option>
                <option value="Vice Principal">Vice Principal</option>
                <option value="Head of Department (HOD)">Head of Department (HOD)</option>
                <option value="Professor">Professor</option>
                <option value="Associate Professor">Associate Professor</option>
                <option value="Assistant Professor">Assistant Professor</option>
                <option value="Lecturer">Lecturer</option>
                <option value="Lab Instructor / Lab Assistant">
                  Lab Instructor / Lab Assistant
                </option>
                <option value="Tutor / Teaching Assistant">Tutor / Teaching Assistant</option>
              </TextField>
            </Grid>

            {/* Qualification */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Qualification"
                size="medium"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.qualification}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    qualification: e.target.value,
                  })
                }
                SelectProps={{ native: true }}
              >
                <option value="">Select Qualification</option>
                <option value="SSC (10th Class)">SSC (10th Class)</option>
                <option value="Intermediate (12th / HSC)">Intermediate (12th / HSC)</option>
                <option value="ITI (Industrial Training Institute)">
                  ITI (Industrial Training Institute)
                </option>
                <option value="Diploma">Diploma</option>
                <option value="B.A (Bachelor of Arts)">B.A (Bachelor of Arts)</option>
                <option value="B.Sc (Bachelor of Science)">B.Sc (Bachelor of Science)</option>
                <option value="B.Com (Bachelor of Commerce)">B.Com (Bachelor of Commerce)</option>
                <option value="BBA (Bachelor of Business Administration)">
                  BBA (Bachelor of Business Administration)
                </option>
                <option value="BCA (Bachelor of Computer Applications)">
                  BCA (Bachelor of Computer Applications)
                </option>
                <option value="B.Tech (Bachelor of Technology)">
                  B.Tech (Bachelor of Technology)
                </option>
                <option value="B.Ed (Bachelor of Education)">B.Ed (Bachelor of Education)</option>
                <option value="M.A (Master of Arts)">M.A (Master of Arts)</option>
                <option value="M.Sc (Master of Science)">M.Sc (Master of Science)</option>
                <option value="M.Com (Master of Commerce)">M.Com (Master of Commerce)</option>
                <option value="MBA (Master of Business Administration)">
                  MBA (Master of Business Administration)
                </option>
                <option value="MCA (Master of Computer Applications)">
                  MCA (Master of Computer Applications)
                </option>
                <option value="M.Tech (Master of Technology)">M.Tech (Master of Technology)</option>
                <option value="M.Ed (Master of Education)">M.Ed (Master of Education)</option>
                <option value="Ph.D (Doctor of Philosophy)">Ph.D (Doctor of Philosophy)</option>
                <option value="DCA (Diploma in Computer Applications)">
                  DCA (Diploma in Computer Applications)
                </option>
                <option value="PGDCA (Post Graduate Diploma in Computer Applications)">
                  PGDCA (Post Graduate Diploma in Computer Applications)
                </option>
              </TextField>
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                required
                size="medium"
                type="email"
                fullWidth
                sx={textFieldSx}
                value={staffFormData.email}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    email: e.target.value,
                  })
                }
                error={!!staffFormErrors.email}
                helperText={staffFormErrors.email}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm Email"
                required
                type="email"
                size="medium"
                fullWidth
                sx={textFieldSx}
                value={staffFormData.confirm_email}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    confirm_email: e.target.value,
                  })
                }
                error={!!staffFormErrors.confirm_email}
                helperText={staffFormErrors.confirm_email}
                {...preventCopyPaste}
              />
            </Grid>

            {/* Aadhar */}

            {/* Mobile */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mobile Number"
                required
                fullWidth
                size="medium"
                type="number"
                sx={textFieldSx}
                value={staffFormData.personal_mobile}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    personal_mobile: e.target.value,
                  })
                }
                error={!!staffFormErrors.personal_mobile}
                helperText={staffFormErrors.personal_mobile}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm Mobile Number"
                required
                fullWidth
                size="medium"
                type="number"
                sx={textFieldSx}
                value={staffFormData.confirm_personal_mobile}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    confirm_personal_mobile: e.target.value,
                  })
                }
                error={!!staffFormErrors.confirm_personal_mobile}
                helperText={staffFormErrors.confirm_personal_mobile}
                {...preventCopyPaste}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Aadhar Number"
                required
                fullWidth
                size="medium"
                type="number"
                sx={textFieldSx}
                value={staffFormData.aadhar_number}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    aadhar_number: e.target.value,
                  })
                }
                error={!!staffFormErrors.aadhar_number}
                helperText={staffFormErrors.aadhar_number}
              />
            </Grid>
            {/* Alternative Mobile */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Alternative Mobile"
                fullWidth
                size="medium"
                type="number"
                value={staffFormData.alternative_mobile}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    alternative_mobile: e.target.value,
                  })
                }
                error={!!staffFormErrors.alternative_mobile}
                helperText={staffFormErrors.alternative_mobile}
              />
            </Grid>

            {/* Personal Address - Full Width */}
            <Grid item xs={12}>
              <TextField
                label="Personal Address"
                fullWidth
                size="medium"
                multiline
                sx={textFieldSx}
                rows={3}
                value={staffFormData.personal_address}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    personal_address: e.target.value,
                  })
                }
              />
            </Grid>

            {/* College Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="College Name"
                fullWidth
                size="medium"
                value={staffFormData.college_name}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    college_name: e.target.value,
                  })
                }
              />
            </Grid>

            {/* College Address */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="College Address"
                fullWidth
                multiline
                size="medium"
                rows={3}
                value={staffFormData.college_address}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    college_address: e.target.value,
                  })
                }
              />
            </Grid>

            {/* Referenced By */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Referenced By"
                fullWidth
                size="medium"
                value={staffFormData.referenced_by}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    referenced_by: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="medium"
                label="Department"
                name="department_id"
                value={staffFormData.department_id || ""}
                onChange={(e) =>
                  setStaffFormData((prev) => ({
                    ...prev,
                    department_id: e.target.value,
                  }))
                }
                required
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select Department</option>

                {departments.map((department) => (
                  <option key={department.department_id} value={department.department_id}>
                    {department.department_name}
                  </option>
                ))}
              </TextField>
            </Grid>

            {/* Course Code */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={courseCodes}
                getOptionLabel={(option) => option.course_code || ""}
                value={
                  courseCodes.find((course) => course.course_code === staffFormData.course_code) ||
                  null
                }
                onChange={(event, newValue) => {
                  setStaffFormData({
                    ...staffFormData,
                    course_code: newValue ? newValue.course_code : "",
                  });
                }}
                loading={courseCodesLoading}
                isOptionEqualToValue={(option, value) => option.course_code === value.course_code}
                filterOptions={(options, { inputValue }) =>
                  options.filter(
                    (option) =>
                      option.course_code.toLowerCase().includes(inputValue.toLowerCase()) ||
                      (option.course_name &&
                        option.course_name.toLowerCase().includes(inputValue.toLowerCase()))
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Course Code"
                    fullWidth
                    helperText="Search and select a course code"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {option.course_code}
                      </Typography>

                      {option.course_name && (
                        <Typography variant="body2" color="text.secondary">
                          {option.course_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              />
            </Grid>

            {/* Staff Status */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Staff Status"
                fullWidth
                size="medium"
                InputLabelProps={{ shrink: true }}
                value={staffFormData.staff_status}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    staff_status: e.target.value,
                  })
                }
                SelectProps={{ native: true }}
              >
                <option value="">Staff Status</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </TextField>
            </Grid>

            {/* Workflow Status */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Workflow Status"
                fullWidth
                size="medium"
                InputLabelProps={{ shrink: true }}
                value={staffFormData.workflow_status}
                onChange={(e) =>
                  setStaffFormData({
                    ...staffFormData,
                    workflow_status: e.target.value,
                  })
                }
                SelectProps={{ native: true }}
              >
                <option value="">Workflow Status</option>
                <option value="DRAFT">Draft</option>
                <option value="APPROVED">Approved</option>
                <option value="INACTIVE">Inactive</option>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              ml: 2,
              backgroundColor: "#ff9800",
              color: "#fff",
              "&:hover": { backgroundColor: "#c77700" },
            }}
          >
            {editing ? "Update Staff" : "Add New Staff"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
